import React, { useMemo, useState } from "react";
import { fetchVendorAnalytics } from "../api/vendorAnalytics";
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

const METRIC_HELP = {
  active_cards_30d: "Cards with at least one give_point or reward in the last 30 days.",
  redemption_rate: "Cards that completed a reward / cards that joined (ratio).",
  returning_users_rate: "Cards that received points on >1 distinct day (ratio).",
  dropoff_rate: "Cards that joined but never received a point (ratio).",
  cards_total: "All cards issued for this vendor.",
  almost_reward_1: "Cards missing 1 point to a reward.",
  almost_reward_2: "Cards missing 2 points to a reward.",
  dormant_gt7: "Cards with no give_point in >7 days.",
  dormant_gt14: "Cards with no give_point in >14 days.",
};

const COLOR = {
  join_card: "#7c6cff",
  give_point: "#00bfa5",
  reward_cycle_completed: "#ff6b6b",
};

function formatNumber(val) {
  if (val === null || val === undefined) return "-";
  if (typeof val === "number" && Math.abs(val) < 1 && val !== 0) return val.toFixed(3);
  if (typeof val === "number") return Number(val.toFixed(2));
  return val;
}

function MetricCard({ label, value, help }) {
  return (
    <div className="analytics-card shadow-sm">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div>
          <div className="fw-semibold">{label}</div>
          <small className="text-muted">{help}</small>
        </div>
        <div className="analytics-value">{formatNumber(value)}</div>
      </div>
    </div>
  );
}

function buildDailySeries(dailyCounts = {}) {
  const today = new Date();
  const days = [];
  for (let i = 27; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const entry = dailyCounts[key] || {};
    days.push({
      key,
      label: key.slice(5), // MM-DD
      join_card: entry.join_card || 0,
      give_point: entry.give_point || 0,
      reward_cycle_completed: entry.reward_cycle_completed || 0,
    });
  }
  return days;
}

function buildWeeklySeries(weeklyCounts = {}) {
  return Object.keys(weeklyCounts)
    .sort()
    .map((wk) => ({
      key: wk,
      label: wk,
      join_card: weeklyCounts[wk]?.join_card || 0,
      give_point: weeklyCounts[wk]?.give_point || 0,
      reward_cycle_completed: weeklyCounts[wk]?.reward_cycle_completed || 0,
    }));
}

export default function VendorAnalyticsPanel({ getToken }) {
  const [vendorId, setVendorId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetchVendorAnalytics(vendorId.trim(), getToken);
      setData(res);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load analytics.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const vendor = data?.vendors?.[0];
  const metrics = vendor?.analytics || {};
  const daily = vendor?.daily_counts || {};
  const weekly = vendor?.weekly_counts || {};

  return (
    <div className="analytics-panel mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="m-0">Vendor Analytics (Admin)</h4>
        <small className="text-muted">Fetch metrics and 28d/4w trends by vendor ID</small>
      </div>
      <form className="row g-2 align-items-end" onSubmit={handleSubmit}>
        <div className="col-md-6">
          <label className="form-label">Vendor ID</label>
          <input
            type="text"
            className="form-control"
            placeholder="68b7...b28"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            required
          />
        </div>
        <div className="col-md-3">
          <button className="btn btn-primary w-100" type="submit" disabled={loading}>
            {loading ? "Loading..." : "Fetch analytics"}
          </button>
        </div>
        {error && <div className="col-12 text-danger small">{error}</div>}
      </form>

      {vendor && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="m-0">Vendor: {vendor.vendor_id}</h5>
            <small className="text-muted">As of {vendor.as_of || "n/a"}</small>
          </div>

          <div className="analytics-grid">
            {Object.entries(METRIC_HELP).map(([key, help]) => (
              <MetricCard key={key} label={key.replace(/_/g, " ")} value={metrics[key]} help={help} />
            ))}
          </div>

          <div className="mt-4">
            <ChartBlock
              title="Daily Loyalty Events (Last 28 Days)"
              data={buildDailySeries(daily)}
              xKey="label"
              keys={["join_card", "reward_cycle_completed", "give_point"]}
            />
          </div>

          <div className="mt-4">
            <ChartBlock
              title="Weekly Loyalty Events (Last 4 Weeks)"
              data={buildWeeklySeries(weekly)}
              xKey="label"
              keys={["join_card", "reward_cycle_completed", "give_point"]}
            />
            {Object.keys(weekly).length === 0 && <div className="text-muted mt-2">No weekly data yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function ChartBlock({ title, data, xKey, keys }) {
  return (
    <div className="chart-card">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="m-0">{title}</h6>
        <div className="d-flex gap-3 small text-muted align-items-center flex-wrap">
          {keys.map((k) => (
            <span key={k} className="d-inline-flex align-items-center gap-1">
              <span className="legend-dot" style={{ background: COLOR[k] }} />
              {k.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <ReLineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            <Legend />
            {keys.map((k) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={COLOR[k]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </ReLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
